/**
 * Frontend Permissions Utility
 * Comprehensive RBAC for Retail ERP
 * 
 * Roles:
 * - Owner: Full access
 * - Salesman: Customer invoices & drafts
 * - WarehouseStaff: Inventory only (Products, Stock, Brands)
 * - AccountsStaff: Finance, Expenses, Reports only
 * - Cashier: Legacy sales role (uses customer invoices)
 * - StoreStaff: Inventory view (legacy)
 * - Accountant: Finance (legacy)
 * - SaaSAdmin: Platform-level only
 */

/**
 * Check if user has a specific role
 * @param {String} userRole - Current user's role
 * @param {String|Array} allowedRoles - Role(s) allowed
 * @returns {Boolean}
 */
export const hasRole = (userRole, allowedRoles) => {
    if (!userRole) return false;

    const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
    return roles.includes(userRole);
};

/**
 * Check if user is Owner
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const isOwner = (userRole) => {
    return userRole === 'Owner';
};

/**
 * Check if user is an admin (Owner or hierarchy admin)
 * Hierarchy admins have full access like Owner
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const isAdmin = (userRole) => {
    return ['Owner', 'CompanyAdmin', 'SoleDistributorAdmin', 'SubDistributorAdmin'].includes(userRole);
};

/**
 * Check if user is Cashier
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const isCashier = (userRole) => {
    return userRole === 'Cashier';
};

/**
 * Check if user is Store Staff
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const isStoreStaff = (userRole) => {
    return userRole === 'StoreStaff';
};

/**
 * Check if user is Accountant
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const isAccountant = (userRole) => {
    return userRole === 'Accountant';
};

/**
 * Check if user is SaaS Admin
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const isSaaSAdmin = (userRole) => {
    return userRole === 'SaaSAdmin';
};

/**
 * Check if user is Salesman
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const isSalesman = (userRole) => {
    return userRole === 'Salesman';
};

/**
 * Check if user is WarehouseStaff
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const isWarehouseStaff = (userRole) => {
    return userRole === 'WarehouseStaff';
};

/**
 * Check if user is AccountsStaff
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const isAccountsStaff = (userRole) => {
    return userRole === 'AccountsStaff';
};

/**
 * Check if user can create sales via customer invoices
 */
export const canAccessSales = (userRole) => {
    if (isAdmin(userRole)) return true;
    return hasRole(userRole, ['Cashier', 'Salesman', 'AccountsStaff', 'Accountant']);
};
/**
 * Check if user can view inventory
 * Admins, KPO, warehouse/store staff can view inventory (read-only for KPO).
 */
export const canViewInventory = (userRole) => {
    if (isAdmin(userRole)) return true;
    return hasRole(userRole, ['StoreStaff', 'WarehouseStaff', 'AccountsStaff', 'Accountant']);
};

/**
 * Check if user can manage inventory (add/edit/delete products)
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const canManageInventory = (userRole) => {
    // Admins can manage inventory
    if (isAdmin(userRole)) return true;
    return hasRole(userRole, ['WarehouseStaff']);
};

/**
 * Check if user can do stock adjustments (stock in/out)
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const canAdjustStock = (userRole) => {
    if (isAdmin(userRole)) return true;
    return hasRole(userRole, ['StoreStaff', 'WarehouseStaff']);
};

/**
 * Check if user can access finance/accounting
 * Only: Owner, AccountsStaff, Accountant, hierarchy admins
 * Salesman and WarehouseStaff CANNOT access finance
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const canAccessFinance = (userRole) => {
    // Admins can access finance
    if (isAdmin(userRole)) return true;
    // AccountsStaff and legacy Accountant can access
    return hasRole(userRole, ['Accountant', 'AccountsStaff']);
};

/**
 * Check if user can access reports
 * @param {String} userRole - Current user's role
 * @param {String} reportType - Type of report ('general', 'finance', 'inventory', 'lowStock')
 * @returns {Boolean}
 */
export const canAccessReports = (userRole, reportType = 'general') => {
    // All admins can access all reports
    if (isAdmin(userRole)) {
        return true;
    }

    // AccountsStaff can access finance, sales, and inventory reports (same as distributors)
    if (userRole === 'AccountsStaff') {
        return ['general', 'finance', 'profitLoss', 'trialBalance', 'balanceSheet', 'supplier', 'sales', 'inventory', 'lowStock'].includes(reportType);
    }

    // Legacy Accountant can access finance and inventory reports
    if (userRole === 'Accountant') {
        return ['general', 'finance', 'profitLoss', 'trialBalance', 'balanceSheet', 'supplier', 'inventory', 'lowStock'].includes(reportType);
    }

    // WarehouseStaff can only see inventory reports
    if (userRole === 'WarehouseStaff') {
        return ['inventory', 'lowStock'].includes(reportType);
    }

    // Legacy StoreStaff can only see inventory/low-stock reports
    if (userRole === 'StoreStaff') {
        return ['inventory', 'lowStock'].includes(reportType);
    }

    // Sales hierarchy — sales / AR reports for their territory (scoped by API)
    if (['NSM', 'RSM', 'ZSM', 'ASM'].includes(userRole)) {
        return ['general', 'sales', 'finance'].includes(reportType);
    }

    // Salesman has no report access
    // Cashier has no report access
    return false;
};

/**
 * Check if user can manage users
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const canManageUsers = (userRole) => {
    return hasRole(userRole, [
        'Owner',
        'SaaSAdmin',
        'CompanyAdmin',
        'SoleDistributorAdmin',
        'SubDistributorAdmin'
    ]);
};

/**
 * Check if user can access business settings
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const canManageBusiness = (userRole) => {
    return hasRole(userRole, [
        'Owner',
        'SaaSAdmin',
        'CompanyAdmin',
        'SoleDistributorAdmin',
        'SubDistributorAdmin'
    ]);
};

/**
 * Check if user can access platform features (SaaS Admin)
 * @param {String} userRole - Current user's role
 * @returns {Boolean}
 */
export const canAccessPlatform = (userRole) => {
    return userRole === 'SaaSAdmin';
};

/**
 * Get allowed routes for a role
 * @param {String} userRole - Current user's role
 * @returns {Array} Array of allowed route paths
 */
export const getAllowedRoutes = (userRole) => {
    const routes = ['/dashboard'];

    if (canAccessSales(userRole)) {
        routes.push('/customer-invoices', '/drafts', '/draft-invoices', '/payment-receipts', '/draft-receipts');
    }

    if (canViewInventory(userRole)) {
        routes.push(
            '/inventory/products',
            '/inventory/suppliers',
            '/inventory/stock-movements',
        );
    }

    if (canManageInventory(userRole)) {
        routes.push('/inventory/purchase');
    }

    if (canAccessFinance(userRole)) {
        routes.push(
            '/purchasing',
            '/purchasing/dues',
            '/accounting',
            '/vendors',
            '/customers',
            '/reports'
        );
    }

    if (canAccessReports(userRole, 'finance')) {
        routes.push('/reports/profit-loss', '/reports/trial-balance', '/reports/balance-sheet');
    }

    if (canAccessReports(userRole, 'inventory')) {
        routes.push('/reports/inventory-movement', '/reports/stock-quantity', '/reports/stock-value');
    }

    if (canManageUsers(userRole)) {
        routes.push('/users');
    }

    return routes;
};
